/*
 *
 * PhoneAuthenticator action/reducer
 *
 */

import { createAction, createSlice, PayloadAction } from "@reduxjs/toolkit";
import { push } from "connected-react-router";
import { PhoneResponsePayload } from "types/PhoneAuthenticator";
import { mfaFlow } from "utils/enum";
import { createActionDispatchers } from "utils/reduxHelpers";

export type State = {
  loading: boolean;
  textMessageReceived: PhoneResponsePayload;
  verifyPassCodeSuccess: PhoneResponsePayload;
  currentFlow: mfaFlow;
};

export const initialState: State = {
  loading: false,
  textMessageReceived: null,
  verifyPassCodeSuccess: null,
  currentFlow: mfaFlow.SECURITY,
};

/**
 * create actions WITHOUT reducer (pure action) here
 */
export const pureAction = createAction("phoneAuthenticator/pureAction");
export const submitPassCode = createAction<string>(
  "phoneAuthenticator/submitPassCode"
);
export const sendTextMessage = createAction<string>(
  "phoneAuthenticator/sendTextMessage"
);

const slice = createSlice({
  name: "phoneAuthenticator",
  initialState,
  // create actions with reducer (slice action) here
  reducers: {
    afterReceivingTextMessage(
      state,
      action: PayloadAction<PhoneResponsePayload>
    ) {
      state.loading = false;
      state.textMessageReceived = action.payload;
    },
    afterSubmitPassCode(state, action: PayloadAction<PhoneResponsePayload>) {
      state.loading = false;
      state.verifyPassCodeSuccess = action.payload;
    },
    cancelPhoneAuthenticator(state) {
      state.verifyPassCodeSuccess = null;
      state.textMessageReceived = null;
    },
    updateCurrentFlow(state, action: PayloadAction<mfaFlow>) {
      state.currentFlow = action.payload;
    },
  },
  // reducer for external action
  extraReducers: (builder) => {
    builder.addCase(submitPassCode, (state) => {
      state.loading = true;
    });
    builder.addCase(sendTextMessage, (state) => {
      state.loading = true;
    });
  },
});

/**
 * actions from slice to be exported for saga
 */
export const {
  afterReceivingTextMessage,
  afterSubmitPassCode,
  cancelPhoneAuthenticator,
  updateCurrentFlow,
} = slice.actions;

/**
 * all actions for layout
 */
export const actionProps = createActionDispatchers(
  slice.actions,
  // slice actions
  ["cancelPhoneAuthenticator", "updateCurrentFlow"],
  // pure actions
  {
    pureAction,
    submitPassCode,
    sendTextMessage,
    goToMFAScreen: (redirect) => push(redirect),
  }
);

export default slice.reducer;
